import { useCallback, useEffect, useState } from "react";
import api, { euro, price, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DateRange, { rangeParams } from "../components/DateRange";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, ComposedChart, Line } from "recharts";
import { Package, Target, Wallet, History, TrendingUp, Sparkles, GitMerge, Eye, EyeOff, Pencil, Trash2, X, Layers } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { RepartiManager } from "../components/RepartiManager";

const CAT_COLORS = ["#2C4A3B", "#8B3A3A", "#D4AF37", "#57534E", "#6B8E7A", "#3E5C7E", "#9A6B4F", "#7E6B9A"];

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState({});
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [budgetRows, setBudgetRows] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [budgetHistory, setBudgetHistory] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [budgetMonth, setBudgetMonth] = useState(new Date().toISOString().slice(0, 7));
  const [seeding, setSeeding] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkCat, setBulkCat] = useState("");
  const [editProd, setEditProd] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showReparti, setShowReparti] = useState(false);

  const deleteProduct = (nn, name) => setConfirm({
    title: `Eliminare "${name}"?`, description: "Verrà rimosso da tutte le fatture.", confirmLabel: "Elimina",
    onConfirm: async () => {
      try { await api.delete(`/products/${encodeURIComponent(nn)}`); toast.success("Prodotto eliminato"); load(); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    },
  });

  const toggleSel = (nn) => setSelected((s) => s.includes(nn) ? s.filter((x) => x !== nn) : [...s, nn]);
  const toggleMonitor = async (nn, cur) => {
    await api.put(`/products/${encodeURIComponent(nn)}/meta`, { monitored: !cur });
    toast.success(!cur ? "Monitoraggio attivato" : "Monitoraggio disattivato");
    load();
  };
  const applyBulkCategory = async () => {
    if (!bulkCat) { toast.error("Scegli una categoria"); return; }
    await api.post("/products/bulk-category", { names: selected, category: bulkCat });
    toast.success(`Categoria aggiornata su ${selected.length} prodotti`);
    setSelected([]); setBulkCat(""); load();
  };
  const mergeSelected = () => {
    if (selected.length < 2) { toast.error("Seleziona almeno 2 prodotti"); return; }
    const target = selected[0];
    const tName = products.find((p) => p.normalized_name === target)?.name || target;
    setConfirm({
      title: `Unire ${selected.length} prodotti?`, description: `Verranno accorpati in "${tName}". I dati storici verranno uniti.`, confirmLabel: "Unisci", danger: false,
      onConfirm: async () => { await api.post("/products/merge", { sources: selected, target_name: target }); toast.success("Prodotti uniti"); setSelected([]); load(); },
    });
  };

  const load = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/reports/category-foodcost", { params: rangeParams(from, to) }).then(({ data }) => setReport(data));
    api.get(`/reports/budget-status?month=${budgetMonth}`).then(({ data }) => {
      setBudgetRows(data.budgets);
      const b = {}; data.budgets.forEach((r) => { if (r.budget != null) b[r.category] = r.budget; });
      setBudgets(b);
    });
    api.get("/reports/budget-history?months=12").then(({ data }) => setBudgetHistory(data));
    api.get("/reports/spend-forecast").then(({ data }) => setForecast(data));
  }, [from, to, budgetMonth]);

  const saveBudgets = async (map) => {
    await api.put(`/budgets/${budgetMonth}`, { category_budgets: Object.fromEntries(Object.entries(map).filter(([, v]) => v > 0)) });
    toast.success("Budget salvato");
    load();
  };
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); api.get("/categories/colors").then(({ data }) => setColors(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const saveCategory = async (nn, category) => {
    await api.put(`/products/${encodeURIComponent(nn)}/meta`, { category });
    toast.success("Categoria aggiornata");
    load();
  };
  const saveTarget = async (nn, target_price) => {
    const val = parseFloat(target_price);
    await api.put(`/products/${encodeURIComponent(nn)}/meta`, { target_price: isNaN(val) ? 0 : val });
    toast.success("Prezzo obiettivo salvato");
    load();
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/demo/seed");
      toast.success(data.already ? "Dati demo già presenti" : `Dati demo caricati (${data.invoices} fatture)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const chartData = (report?.by_category || []).map((c) => ({ name: c.category, cost: c.cost, incidence: c.incidence }));
  const budgetCats = Array.from(new Set([...budgetRows.map((r) => r.category), ...((report?.by_category || []).map((c) => c.category))]));

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Reparti</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Prodotti &amp; Categorie</h1>
          <p className="text-sm text-stone-500 mt-2">Assegna una categoria e un prezzo obiettivo a ogni prodotto.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="manage-reparti-btn" onClick={() => setShowReparti(true)}
            className="flex items-center gap-2 rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-white transition-colors">
            <Layers size={16} /> Gestisci reparti
          </button>
          {user?.role === "owner" && (
            <button data-testid="seed-demo-btn" onClick={seedDemo} disabled={seeding}
              className="flex items-center gap-2 rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-white transition-colors disabled:opacity-60">
              <Sparkles size={16} /> {seeding ? "Caricamento…" : "Carica dati demo"}
            </button>
          )}
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <h3 className="font-serif text-2xl text-stone-900 mb-5">Costo per reparto</h3>
        {chartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-stone-400 text-sm">Nessun dato nel periodo selezionato.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="name" stroke="#A8A29E" fontSize={12} />
                <YAxis stroke="#A8A29E" fontSize={12} tickFormatter={(v) => `€${v}`} />
                <Tooltip formatter={(v, n) => n === "cost" ? euro(v) : `${v}%`}
                  contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
                <Bar dataKey="cost" name="Costo" radius={[2, 2, 0, 0]}>
                  {chartData.map((c, i) => <Cell key={c.name} fill={colors[c.name] || CAT_COLORS[i % CAT_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div data-testid="category-pie">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
                  <Legend />
                  <Pie data={chartData} dataKey="cost" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={45} paddingAngle={2}>
                    {chartData.map((c, i) => <Cell key={c.name} fill={colors[c.name] || CAT_COLORS[i % CAT_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {report && report.by_category.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            {report.by_category.map((c, i) => (
              <div key={c.category} data-testid={`cat-stat-${c.category}`} className="border border-stone-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[c.category] || CAT_COLORS[i % CAT_COLORS.length] }} />
                  <span className="text-xs font-semibold text-stone-700">{c.category}</span>
                </div>
                <p className="font-serif text-2xl text-stone-900">{euro(c.cost)}</p>
                <p className="text-xs text-stone-400">Incidenza {c.incidence != null ? `${c.incidence}%` : "—"}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-brand" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-stone-900">Budget mensile per reparto</h3>
          </div>
          <input data-testid="budget-month" type="month" value={budgetMonth} onChange={(e) => setBudgetMonth(e.target.value)}
            className="border border-stone-300 bg-white px-3 py-1.5 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
        </div>
        {budgetCats.length === 0 ? (
          <p className="text-sm text-stone-400">Nessun reparto ancora. Carica delle fatture.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {budgetCats.map((cat) => {
              const spent = budgetRows.find((r) => r.category === cat)?.spent || 0;
              const bud = budgets[cat];
              const over = bud && spent > bud;
              return (
                <div key={cat} data-testid={`budget-row-${cat}`} className={`border rounded-sm p-4 ${over ? "border-alert bg-alert/5" : "border-stone-200"}`}>
                  <p className="text-xs font-semibold text-stone-700 mb-2">{cat}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-400">€</span>
                    <input data-testid={`budget-input-${cat}`} type="number" value={budgets[cat] ?? ""} placeholder="budget mese"
                      onChange={(e) => setBudgets({ ...budgets, [cat]: parseFloat(e.target.value) || 0 })}
                      onBlur={() => saveBudgets(budgets)}
                      className="w-full border border-stone-300 bg-white px-2 py-1 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
                  </div>
                  <p className={`text-xs mt-2 ${over ? "text-alert font-bold" : "text-stone-400"}`}>
                    Speso nel mese: {euro(spent)}{over ? " · superato!" : ""}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {budgetHistory && budgetHistory.rows.length > 0 && (
        <div data-testid="budget-history" className="bg-white border border-stone-200 rounded-sm p-6 overflow-x-auto">
          <div className="flex items-center gap-2 mb-5">
            <History size={18} className="text-brand" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-stone-900">Storico budget (12 mesi)</h3>
          </div>
          <table className="w-full text-xs min-w-[640px]">
            <thead>
              <tr className="text-stone-400">
                <th className="text-left py-2 pr-4 font-semibold">Reparto</th>
                {budgetHistory.months.map((m) => <th key={m} className="px-1 font-medium">{m.slice(5)}/{m.slice(2, 4)}</th>)}
              </tr>
            </thead>
            <tbody>
              {budgetHistory.rows.map((r) => (
                <tr key={r.category} data-testid={`history-row-${r.category}`}>
                  <td className="py-1 pr-4 font-semibold text-stone-700 whitespace-nowrap">{r.category}{r.budget ? ` (€${r.budget})` : ""}</td>
                  {r.cells.map((c) => {
                    const bg = !r.budget ? "#E7E5E4" : c.over ? "#8B3A3A" : c.spent > 0 ? "#2C4A3B" : "#E7E5E4";
                    return <td key={c.month} className="px-1"><div title={`${c.month}: €${c.spent}${r.budget ? ` / €${r.budget}` : ""}`} className="h-6 rounded-sm" style={{ background: bg }} /></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-4 mt-3 text-xs text-stone-500 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#2C4A3B" }} /> Nei limiti</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#8B3A3A" }} /> Superato</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#E7E5E4" }} /> Nessun budget / spesa</span>
          </div>
        </div>
      )}

      {forecast && forecast.by_category.length > 0 && (
        <div data-testid="spend-forecast" className="bg-white border border-stone-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-brand" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl text-stone-900">Previsione spesa ({forecast.month})</h3>
            </div>
            <span className="text-xs text-stone-400">giorno {forecast.days_elapsed}/{forecast.days_in_month}</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={forecast.by_category} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
              <XAxis dataKey="category" stroke="#A8A29E" fontSize={12} />
              <YAxis stroke="#A8A29E" fontSize={12} tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
              <Legend />
              <Bar dataKey="spent" name="Speso" fill="#6B8E7A" radius={[2, 2, 0, 0]} />
              <Bar dataKey="projected" name="Stima fine mese" fill="#2C4A3B" radius={[2, 2, 0, 0]} />
              <Line dataKey="budget" name="Budget" stroke="#8B3A3A" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                <th className="text-left py-3">Reparto</th>
                <th className="text-right py-3">Speso finora</th>
                <th className="text-right py-3">Stima fine mese</th>
                <th className="text-right py-3">Budget</th>
                <th className="text-center py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {forecast.by_category.map((c) => (
                <tr key={c.category} data-testid={`forecast-row-${c.category}`} className="border-b border-stone-100">
                  <td className="py-3 font-semibold text-stone-800">{c.category}</td>
                  <td className="py-3 text-right text-stone-600">{euro(c.spent)}</td>
                  <td className="py-3 text-right font-semibold text-stone-900">{euro(c.projected)}</td>
                  <td className="py-3 text-right text-stone-500">{c.budget != null ? euro(c.budget) : "—"}</td>
                  <td className="py-3 text-center">
                    {c.budget == null ? <span className="text-xs text-stone-400">—</span>
                      : c.over_projected ? <span className="text-xs font-bold text-alert bg-alert/10 px-2 py-1 rounded-full">Sforerà</span>
                      : <span className="text-xs font-bold text-brand bg-brand/10 px-2 py-1 rounded-full">Ok</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-stone-200 flex items-center gap-2">
          <Package size={18} className="text-brand" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-stone-900">I tuoi prodotti</h3>
        </div>
        {selected.length > 0 && (
          <div data-testid="bulk-bar" className="px-6 py-3 bg-brand/5 border-b border-brand/20 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold text-stone-700">{selected.length} selezionati</span>
            <select data-testid="bulk-category-select" value={bulkCat} onChange={(e) => setBulkCat(e.target.value)}
              className="border border-stone-300 bg-white px-3 py-1.5 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40">
              <option value="">Cambia categoria…</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button data-testid="bulk-apply-category" onClick={applyBulkCategory} className="rounded-full bg-brand text-white px-4 py-1.5 text-sm font-semibold hover:bg-brand-hover">Applica categoria</button>
            <button data-testid="bulk-merge" onClick={mergeSelected} className="rounded-full border border-brand text-brand px-4 py-1.5 text-sm font-semibold flex items-center gap-1 hover:bg-brand hover:text-white"><GitMerge size={14} /> Unisci selezionati</button>
            <button data-testid="bulk-clear" onClick={() => setSelected([])} className="text-sm text-stone-500 hover:text-stone-800 ml-auto">Deseleziona</button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
              <th className="py-4 px-4 w-8"></th>
              <th className="text-left font-bold py-4 px-6">Prodotto</th>
              <th className="text-left font-bold py-4 px-6">Categoria</th>
              <th className="text-right font-bold py-4 px-6">Ultimo prezzo</th>
              <th className="text-left font-bold py-4 px-6">Prezzo obiettivo</th>
              <th className="text-center font-bold py-4 px-6">Monitorato</th>
              <th className="text-center font-bold py-4 px-6">Stato</th>
              <th className="text-center font-bold py-4 px-6">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr><td colSpan={8} className="py-16 text-center text-stone-400">Nessun prodotto. Carica delle fatture.</td></tr>
            )}
            {products.map((p) => {
              const over = p.target_price != null && p.last_price > p.target_price;
              return (
                <tr key={p.normalized_name} data-testid={`product-manage-${p.normalized_name}`} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                  <td className="py-4 px-4 text-center">
                    <input type="checkbox" data-testid={`sel-${p.normalized_name}`} checked={selected.includes(p.normalized_name)} onChange={() => toggleSel(p.normalized_name)} className="accent-brand w-4 h-4" />
                  </td>
                  <td className="py-4 px-6 font-semibold text-stone-800">{p.name}</td>
                  <td className="py-4 px-6">
                    <Select value={p.category} onValueChange={(v) => saveCategory(p.normalized_name, v)}>
                      <SelectTrigger data-testid={`cat-select-${p.normalized_name}`} className="w-40 h-9 rounded-sm border-stone-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-6 text-right text-stone-700">{price(p.last_price)}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-1">
                      <Target size={14} className="text-stone-400" />
                      <input data-testid={`target-input-${p.normalized_name}`} type="number" step="0.01"
                        defaultValue={p.target_price ?? ""} placeholder="—"
                        onBlur={(e) => { if (e.target.value !== String(p.target_price ?? "")) saveTarget(p.normalized_name, e.target.value); }}
                        className="w-24 border border-stone-300 bg-white px-2 py-1 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <button data-testid={`monitor-toggle-${p.normalized_name}`} onClick={() => toggleMonitor(p.normalized_name, p.monitored)}
                      title={p.monitored ? "Monitorato — clic per disattivare" : "Non monitorato — clic per attivare"}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold transition-colors ${p.monitored ? "text-brand bg-brand/10 hover:bg-brand/20" : "text-stone-400 bg-stone-100 hover:bg-stone-200"}`}>
                      {p.monitored ? <Eye size={14} /> : <EyeOff size={14} />}
                      {p.monitored ? "Sì" : "No"}
                    </button>
                  </td>
                  <td className="py-4 px-6 text-center">
                    {p.target_price == null ? <span className="text-xs text-stone-400">—</span>
                      : over ? <span data-testid={`over-badge-${p.normalized_name}`} className="text-xs font-bold text-alert bg-alert/10 px-2 py-1 rounded-full">Supera obiettivo</span>
                      : <span className="text-xs font-bold text-brand bg-brand/10 px-2 py-1 rounded-full">Nei limiti</span>}
                  </td>
                  <td className="py-4 px-6 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button data-testid={`edit-product-${p.normalized_name}`} onClick={() => setEditProd(p)} className="text-stone-400 hover:text-brand" title="Modifica"><Pencil size={16} /></button>
                      <button data-testid={`delete-product-${p.normalized_name}`} onClick={() => deleteProduct(p.normalized_name, p.name)} className="text-stone-400 hover:text-alert" title="Elimina"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editProd && <ProductEditDialog product={editProd} categories={categories} onClose={() => setEditProd(null)} onSaved={() => { setEditProd(null); load(); }} />}
      {confirm && <ConfirmDialog open title={confirm.title} description={confirm.description} confirmLabel={confirm.confirmLabel} danger={confirm.danger !== false} onConfirm={async () => { await confirm.onConfirm(); setConfirm(null); }} onClose={() => setConfirm(null)} />}
      {showReparti && <RepartiManager onClose={() => { setShowReparti(false); load(); api.get("/categories/colors").then(({ data }) => setColors(data)).catch(() => {}); }} onChanged={(list) => setCategories(list)} />}
    </div>
  );
}

function ProductEditDialog({ product, categories, onClose, onSaved }) {
  const [name, setName] = useState(product.name || "");
  const [category, setCategory] = useState(product.category || "");
  const [target, setTarget] = useState(product.target_price ?? "");
  const [monitored, setMonitored] = useState(product.monitored !== false);
  const [saving, setSaving] = useState(false);
  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${encodeURIComponent(product.normalized_name)}/edit`, {
        name, category, target_price: target === "" ? 0 : Number(target), monitored,
      });
      toast.success("Prodotto aggiornato");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="product-edit-dialog" className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-stone-900">Modifica prodotto</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Nome</label>
            <input data-testid="edit-product-name" className={`${inp} mt-1.5`} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Categoria</label>
              <select data-testid="edit-product-category" className={`${inp} mt-1.5`} value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Prezzo obiettivo (€)</label>
              <input data-testid="edit-product-target" type="number" step="0.01" min="0" className={`${inp} mt-1.5`} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="—" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700"><input data-testid="edit-product-monitored" type="checkbox" checked={monitored} onChange={(e) => setMonitored(e.target.checked)} /> Monitora questo prodotto</label>
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100">Annulla</button>
          <button data-testid="edit-product-save" onClick={save} disabled={saving} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}
